import { lazy, ReactNode, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import AdminLayout from './components/AdminLayout';
import Layout from './components/Layout';
import { Spinner } from './components/ui';
import { AdminAuthProvider, useAdmin } from './contexts/AdminAuthContext';
import { useAuth } from './contexts/AuthContext';
import Billing from './pages/Billing';
import Login, { homeFor } from './pages/Login';
import { TOKEN_KEY } from './services/api';
import type { Role } from './types';

// Billing loads first; everything else (charts, spreadsheets) on demand.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Products = lazy(() => import('./pages/Products'));
const Stock = lazy(() => import('./pages/Stock'));
const Labels = lazy(() => import('./pages/Labels'));
const Sales = lazy(() => import('./pages/Sales'));
const Reports = lazy(() => import('./pages/Reports'));
const Counters = lazy(() => import('./pages/Counters'));
const Users = lazy(() => import('./pages/Users'));
const Settings = lazy(() => import('./pages/Settings'));
const MySubscription = lazy(() => import('./pages/MySubscription'));
const Signup = lazy(() => import('./pages/Signup'));
const Profile = lazy(() => import('./pages/Profile'));
const AdminProfile = lazy(() => import('./pages/admin/AdminProfile'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminShops = lazy(() => import('./pages/admin/Shops'));
const AdminSubscriptions = lazy(() => import('./pages/admin/Subscriptions'));
const AdminShopTypes = lazy(() => import('./pages/admin/Misc').then((m) => ({ default: m.ShopTypes })));
const AdminFestivals = lazy(() => import('./pages/admin/Misc').then((m) => ({ default: m.Festivals })));
const AdminAudit = lazy(() => import('./pages/admin/Misc').then((m) => ({ default: m.AuditLog })));
const AdminSettings = lazy(() => import('./pages/admin/Misc').then((m) => ({ default: m.PlatformSettings })));

function Shop({ roles, feature, children }: { roles?: Role[]; feature?: string; children: ReactNode }) {
  const { session, loading, isRole, has } = useAuth();
  if (loading) return <Spinner />;
  if (!session || session.must_change_password) return <Navigate to="/login" replace />;
  if ((roles && !isRole(...roles)) || (feature && !has(feature))) return <Navigate to={homeFor(session.user.role, !!session.counter)} replace />;
  return <Layout><Suspense fallback={<Spinner />}>{children}</Suspense></Layout>;
}

function Admin({ children }: { children: ReactNode }) {
  const { admin } = useAdmin();
  if (!admin) return <Navigate to="/admin/login" replace />;
  return <AdminLayout><Suspense fallback={<Spinner />}>{children}</Suspense></AdminLayout>;
}

/** Super Admin's read-only support session, opened from the Shops page. */
function SupportEntry() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    const token = window.location.hash.slice(1);
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      window.history.replaceState(null, '', window.location.pathname);
      refresh().then(() => navigate('/dashboard', { replace: true }));
    } else navigate('/login', { replace: true });
  }, [refresh, navigate]);
  return <Spinner />;
}

const OM: Role[] = ['owner', 'manager'];

export default function App() {
  const { session } = useAuth();
  return (
    <AdminAuthProvider>
      <Suspense fallback={<Spinner />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/support" element={<SupportEntry />} />
          <Route path="/billing" element={<Shop><Billing /></Shop>} />
          <Route path="/dashboard" element={<Shop roles={OM}><Dashboard /></Shop>} />
          <Route path="/products" element={<Shop><Products /></Shop>} />
          <Route path="/stock" element={<Shop roles={OM}><Stock /></Shop>} />
          <Route path="/labels" element={<Shop roles={OM} feature="label_printing"><Labels /></Shop>} />
          <Route path="/sales" element={<Shop><Sales /></Shop>} />
          <Route path="/reports" element={<Shop roles={OM}><Reports /></Shop>} />
          <Route path="/counters" element={<Shop roles={OM}><Counters /></Shop>} />
          <Route path="/users" element={<Shop roles={['owner']}><Users /></Shop>} />
          <Route path="/settings" element={<Shop roles={['owner']}><Settings /></Shop>} />
          <Route path="/subscription" element={<Shop roles={['owner']}><MySubscription /></Shop>} />
          <Route path="/profile" element={<Shop><Profile /></Shop>} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<Admin><AdminDashboard /></Admin>} />
          <Route path="/admin/shops" element={<Admin><AdminShops /></Admin>} />
          <Route path="/admin/subscriptions" element={<Admin><AdminSubscriptions /></Admin>} />
          <Route path="/admin/shop-types" element={<Admin><AdminShopTypes /></Admin>} />
          <Route path="/admin/festivals" element={<Admin><AdminFestivals /></Admin>} />
          <Route path="/admin/audit" element={<Admin><AdminAudit /></Admin>} />
          <Route path="/admin/settings" element={<Admin><AdminSettings /></Admin>} />
          <Route path="/admin/profile" element={<Admin><AdminProfile /></Admin>} />
          <Route path="*" element={<Navigate to={session ? homeFor(session.user.role, !!session.counter) : '/login'} replace />} />
        </Routes>
      </Suspense>
    </AdminAuthProvider>
  );
}
